/*
 * The layout editor's panel (docked, not a dialog), for a grid of any
 * structure dragged out with Shift or Alt, and for planning mines over
 * prospected ground. Once a box is down: its options, what it comes to
 * (how many, track, steel), and Build all or Cancel. The map shows the
 * layout as ghosts and the box with handles to adjust it.
 */

import { For, Show, createSignal, type JSX } from "solid-js";
import { render } from "solid-js/web";
import { ORE_HUES, oreLabel } from "../render/colour";

export interface MinePlan {
  /** A grid laid out here, or mines planned by the server on known ore. */
  mode: "grid" | "mines";
  /** What a grid is of (its name, for the panel). */
  name: string;
  /** The box, corner to corner (hexes). */
  box: [[number, number], [number, number]];
  /** Mines: join them to your track. Grid: a spine of track along it. */
  rail: boolean;
  /** Mines: "" for every ore. */
  ore: string;
  /** Grid: hexes between spots, rows offset like hexes or straight, and
   *  whether a spot that doesn't fit moves to one nearby that does. */
  spacing: number;
  stagger: boolean;
  nudge: boolean;
  /** The spots (mines: best first) and the track joining them. */
  mines: [number, number][];
  track: [[number, number], [number, number]][];
  /** Grid: spots that don't fit (left out), and its rows and columns. */
  unfit: [number, number][];
  rows: number;
  cols: number;
  /** Each mine's ores, in words. */
  labels: string[];
  /** Waiting for the server's plan. */
  waiting: boolean;
  /** Steel it would take (free builds counted). */
  steel: number;
  trackHexes: number;
  /** The server's word if it couldn't plan. */
  error: string;
  /** Mines mode reached from a grid of mines (unticking goes back). */
  fromGrid: boolean;
  /** Mines: how many of the proposed to build (the best, iron and coal
   *  alternating), and how many were proposed. */
  count?: number;
  total?: number;
}

const [plan, setPlan] = createSignal<MinePlan | null>(null);
export { plan, setPlan };

let handlers = {
  change: (_c: Partial<MinePlan>) => {},
  build: () => {},
  cancel: () => {},
};
export function setPlanHandlers(h: typeof handlers): void {
  handlers = h;
}

const ORES = ["", ...ORE_HUES.map(([o]) => o)];

function MinePlanPanel(): JSX.Element {
  return (
    <Show when={plan()}>
      {(p) => (
        <aside class="plan-card" aria-label={p().mode === "mines" ? "Mine plan" : "Grid"}>
          <h2>{p().mode === "mines" ? "Mine plan" : `Grid of ${p().name.toLowerCase()}${/s$/.test(p().name) ? "" : "s"}`}</h2>
          {/* A grid of mines can go only where ore is known (the planner). */}
          <Show when={p().mode === "mines" ? p().fromGrid : p().name === "Mine"}>
            <label class="plan-option">
              <input type="checkbox" checked={p().mode === "mines"} onChange={(e) => handlers.change({ mode: e.currentTarget.checked ? "mines" : "grid" })} />
              Only on known ore <span class="muted small">the best spots</span>
            </label>
          </Show>
          <Show when={p().mode === "grid"}>
            <label class="plan-option">
              Spacing
              <input
                type="number"
                min="1"
                max="60"
                value={p().spacing}
                onChange={(e) => handlers.change({ spacing: Math.max(1, Math.min(60, Math.floor(Number(e.currentTarget.value) || 1))) })}
              />
              <span class="muted small">hexes</span>
            </label>
            <div class="segmented" role="radiogroup" aria-label="Layout">
              <button role="radio" aria-checked={p().stagger} onClick={() => handlers.change({ stagger: true })} data-tip="Every other row offset: closest packing on hexes">
                Offset rows
              </button>
              <button role="radio" aria-checked={!p().stagger} onClick={() => handlers.change({ stagger: false })} data-tip="Rows straight above each other">
                Straight rows
              </button>
            </div>
            <label class="plan-option">
              <input type="checkbox" checked={p().nudge} onChange={(e) => handlers.change({ nudge: e.currentTarget.checked })} />
              Move unfit spots <span class="muted small">to a hex nearby that fits</span>
            </label>
          </Show>
          <label class="plan-option">
            <input type="checkbox" checked={p().rail} onChange={(e) => handlers.change({ rail: e.currentTarget.checked })} />
            {p().mode === "mines" ? "Rail layout" : "Connect with track"}{" "}
            <span class="muted small">{p().mode === "mines" ? "joining them to your track" : "along each row, rows joined"}</span>
          </label>
          <Show when={p().mode === "mines"}>
            <label class="plan-option">
              Ore
              <select value={p().ore} onChange={(e) => handlers.change({ ore: e.currentTarget.value })}>
                <For each={ORES}>{(o) => <option value={o}>{o ? oreLabel(o) : "Any"}</option>}</For>
              </select>
            </label>
          </Show>
          <Show when={!p().waiting} fallback={<p class="muted">Planning…</p>}>
            <Show
              when={p().mines.length}
              fallback={<p class="muted">{p().error || (p().mode === "mines" ? "No mines fit there on ore you've prospected." : "Nothing fits there.")}</p>}
            >
              <p>
                <Show when={p().mode === "grid"}>
                  {p().rows} × {p().cols} ·{" "}
                </Show>
                {p().mines.length} to build
                <Show when={p().unfit.length}>
                  <span class="muted"> ({p().unfit.length} don't fit)</span>
                </Show>
                <Show when={p().rail && p().trackHexes}> · {p().trackHexes.toLocaleString("en-US")} hexes of track</Show>
                <span class="muted"> · ≈ {p().steel.toLocaleString("en-US")} steel</span>
              </p>
              <Show when={p().mode === "mines" && (p().total ?? 0) > 1}>
                <label class="plan-option">
                  Build the best
                  <input
                    type="number"
                    min="1"
                    max={p().total}
                    value={p().count ?? p().total}
                    onChange={(e) => handlers.change({ count: Math.max(1, Math.min(p().total ?? 1, Math.floor(Number(e.currentTarget.value) || 1))) })}
                  />
                  <span class="muted small">of {p().total}</span>
                </label>
              </Show>
              <Show when={p().mode === "mines"}>
                <ul class="plan-mines">
                  <For each={p().labels.slice(0, 8)}>{(l, i) => <li>{`${p().mines[i()][0]}, ${p().mines[i()][1]}: ${l}`}</li>}</For>
                  <Show when={p().labels.length > 8}>
                    <li class="muted">and {p().labels.length - 8} more</li>
                  </Show>
                </ul>
              </Show>
            </Show>
          </Show>
          <p class="muted small">Drag the box's corners to change it. Right-click or Esc cancels.</p>
          <div class="plan-actions">
            <button class="ghost" onClick={() => handlers.cancel()}>
              Cancel
            </button>
            <button class="primary" disabled={p().waiting || !p().mines.length} onClick={() => handlers.build()}>
              {p().mode === "mines" ? `Build ${p().mines.length}` : "Build all"}
            </button>
          </div>
        </aside>
      )}
    </Show>
  );
}

const host = document.createElement("div");
host.id = "mine-plan";
document.body.append(host);
render(() => <MinePlanPanel />, host);
